import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ButtonPrimary, ButtonOutline } from '../components/Button';
import heroBg from '../assets/hero.png';

/* ── Animations ─────────────────────────────────────── */
const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(36px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const gridScroll = keyframes`
  0%   { transform: translateY(0); }
  100% { transform: translateY(64px); }
`;
const particleFloat = keyframes`
  0%   { transform: translateY(100vh) translateX(0);  opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateY(-40px) translateX(20px); opacity: 0; }
`;
const scrollPulse = keyframes`
  0%   { transform: scaleY(0); transform-origin: top; }
  50%  { transform: scaleY(1); transform-origin: top; }
  51%  { transform: scaleY(1); transform-origin: bottom; }
  100% { transform: scaleY(0); transform-origin: bottom; }
`;
const dotPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.75); }
`;
const glitch1 = keyframes`
  0%, 88%, 100% { transform: translate(0); }
  90% { transform: translate(-4px, 1px); }
  93% { transform: translate(4px, -1px); }
  96% { transform: translate(-2px, 2px); }
`;
const glitch2 = keyframes`
  0%, 88%, 100% { transform: translate(0); }
  91% { transform: translate(4px, -1px); }
  94% { transform: translate(-4px, 1px); }
  97% { transform: translate(2px, -2px); }
`;

/* ── Styled ──────────────────────────────────────────── */
const Section = styled.section`
  position: relative;
  height: 100vh;
  min-height: 700px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  background: url(${heroBg}) center center / cover no-repeat;
  opacity: 0.22;
  transform: scale(1.04);
`;

const BgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.gradients.heroOverlay},
    linear-gradient(to bottom, rgba(2,2,14,0.1) 0%, rgba(2,2,14,0.6) 80%, rgba(2,2,14,1) 100%);
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0,163,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,163,255,0.04) 1px, transparent 1px);
  background-size: 64px 64px;
  animation: ${gridScroll} 18s linear infinite;
`;

const ParticleCanvas = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Particle = styled.div`
  position: absolute;
  border-radius: 50%;
  animation: ${particleFloat} ${({ $dur }) => $dur}s linear ${({ $delay }) => $delay}s infinite;
  left: ${({ $left }) => $left}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 6px ${({ $color }) => $color};
  opacity: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 1040px;
  padding: 0 48px;
  @media (max-width: 600px) { padding: 0 20px; }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(0,163,255,0.3);
  padding: 7px 18px;
  margin-bottom: 32px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.32em;
  @media (max-width: 600px) { margin-bottom: 20px; font-size: 8px; letter-spacing: 0.18em; padding: 6px 14px; }
  color: ${({ theme }) => theme.colors.blue};
  text-transform: uppercase;
  animation: ${fadeDown} 0.9s ease forwards;
  opacity: 0;
`;

const BadgeDot = styled.span`
  width: 6px; height: 6px;
  background: ${({ theme }) => theme.colors.blue};
  border-radius: 50%;
  display: inline-block;
  animation: ${dotPulse} 2.2s ease infinite;
`;

const TitleWrap = styled.div`
  animation: ${fadeUp} 0.9s ease 0.3s forwards;
  opacity: 0;
  margin-bottom: 24px;
`;

const TitleLine1 = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(80px, 14vw, 190px);
  line-height: 0.85;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.65) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;

  &::before {
    content: attr(data-text);
    position: absolute; top: 0; left: 0; width: 100%;
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: inherit; line-height: inherit;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.blue}, transparent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    clip-path: polygon(0 20%, 100% 20%, 100% 50%, 0 50%);
    animation: ${glitch1} 4s infinite;
    opacity: 0.6;
  }
  &::after {
    content: attr(data-text);
    position: absolute; top: 0; left: 0; width: 100%;
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: inherit; line-height: inherit;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.magenta}, transparent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    clip-path: polygon(0 60%, 100% 60%, 100% 80%, 0 80%);
    animation: ${glitch2} 4s infinite;
    opacity: 0.45;
  }
`;

const TitleLine2 = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(80px, 14vw, 190px);
  line-height: 0.85;
  letter-spacing: 0.02em;
  background: ${({ theme }) => theme.gradients.brand};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 50px rgba(0,163,255,0.45));
`;

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.72);
  margin-bottom: 40px;
  @media (max-width: 600px) { font-size: 10px; letter-spacing: 0.14em; margin-bottom: 28px; }
  animation: ${fadeUp} 0.9s ease 0.5s forwards;
  opacity: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  @media (max-width: 480px) { flex-direction: column; align-items: center; gap: 10px; }
  animation: ${fadeUp} 0.9s ease 0.7s forwards;
  opacity: 0;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  @media (max-width: 600px) { display: none; }
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  animation: ${fadeIn} 1s ease 1.2s forwards;
  opacity: 0;
`;

const ScrollText = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.35em;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 64px;
  background: linear-gradient(to bottom, ${({ theme }) => theme.colors.blue}, transparent);
  animation: ${scrollPulse} 2.2s ease infinite;
`;

/* ── Particles data ──────────────────────────────────── */
const particles = Array.from({ length: 44 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  dur: 9 + Math.random() * 14,
  delay: Math.random() * 16,
  size: 1 + Math.random() * 3,
  color: Math.random() > 0.5 ? '#00A3FF' : '#C800FF',
}));

export default function Hero() {
  const { t } = useTranslation();

  return (
    <Section id="hero">
      <BgImage />
      <BgOverlay />
      <Grid />
      <ParticleCanvas>
        {particles.map(p => (
          <Particle key={p.id} $left={p.left} $dur={p.dur} $delay={p.delay} $size={p.size} $color={p.color} />
        ))}
      </ParticleCanvas>

      <Content>
        <Badge>
          <BadgeDot /> {t('hero.badge')}
        </Badge>

        <TitleWrap>
          <TitleLine1 data-text={t('hero.line1')}>{t('hero.line1')}</TitleLine1>
          <TitleLine2>{t('hero.line2')}</TitleLine2>
        </TitleWrap>

        <Subtitle>{t('hero.subtitle')}</Subtitle>

        <Actions>
          <ButtonPrimary as={Link} to="/games">▶ &nbsp;{t('hero.btnGames')}</ButtonPrimary>
          <ButtonOutline as={Link} to="/engine">{t('hero.btnEngine')}</ButtonOutline>
          <ButtonOutline
            href="https://store.games.guebly.com.br"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('hero.btnStore')}
          </ButtonOutline>
        </Actions>
      </Content>

      <ScrollIndicator>
        <ScrollText>{t('hero.scrollLabel')}</ScrollText>
        <ScrollLine />
      </ScrollIndicator>
    </Section>
  );
}
